import React, { useState, useEffect } from "react";
import AceEditor from "react-ace";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Textarea } from "../ui/textarea";
import "brace/mode/json";
import "brace/theme/github";
import { useToast } from "../ui/use-toast";

interface JsonOutputFieldProps {
    apis: any[];
    setApis: (apis: any[]) => void;
    preview?: boolean;
    output: {
        status: string;
        body: object | string;
        description: string;
    };
    index: number;
    io: number;
}

const JsonOutputField: React.FC<JsonOutputFieldProps> = ({
                                                             apis,
                                                             setApis,
                                                             output,
                                                             index,
                                                             io,
                                                         }) => {
    const { toast } = useToast();

    // Helper to derive initial code from the prop "output.body"
    const getInitialCode = () => {
        if (typeof output.body === "string") {
            return output.body;
        }
        if (Object.keys(output.body || {}).length === 0) {
            return "";
        }
        return JSON.stringify(output.body, null, 2);
    };

    const [code, setCode] = useState(getInitialCode());

    // Update local state whenever output.body changes.
    useEffect(() => {
        setCode(getInitialCode());
    }, [output.body]);

    const handleCodeChange = (newCode: string) => {
        setCode(newCode);
    };

    const validateCodeChange = () => {
        try {
            // Try parsing and formatting JSON
            const parsed = JSON.parse(code);
            const formatted = JSON.stringify(parsed, null, 2);
            setApis(
                apis.map((apiObj, i) =>
                    i === index
                        ? {
                            ...apiObj,
                            output: apiObj.output.map((out: any, j: number) =>
                                j === io
                                    ? { ...out, body: parsed, description: output.description || "" }
                                    : out
                            ),
                        }
                        : apiObj
                )
            );
            setCode(formatted);
        } catch (error) {
            // If parsing fails, save raw text
            setApis(
                apis.map((apiObj, i) =>
                    i === index
                        ? {
                            ...apiObj,
                            output: apiObj.output.map((out: any, j: number) =>
                                j === io ? { ...out, body: code, description: output.description || "" } : out
                            ),
                        }
                        : apiObj
                )
            );
            toast({
                description: "Not valid JSON – saved as plain text.",
                className: "bg-yellow-500 text-white",
            });
        }
    };

    return (
        <Accordion
            type="single"
            collapsible
            className="w-full"
            defaultValue={"output-json-" + index + "-" + io}
        >
            <AccordionItem value={"output-json-" + index + "-" + io} className="mt-4">
                <AccordionTrigger className="text-white text-sm inputoutput font-bold tracking-wide">
                    JSON
                </AccordionTrigger>
                <AccordionContent>
                    <AceEditor
                        style={{ height: "auto", minHeight: "100px" }}
                        mode="json"
                        theme="tomorrow"
                        value={code}
                        onChange={handleCodeChange}
                        onBlur={validateCodeChange}
                        name={"UNIQUE_OUTPUT_DIV_" + index + "_" + io}
                        editorProps={{ $blockScrolling: true }}
                        setOptions={{
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </AccordionContent>
            </AccordionItem>
            <AccordionItem value={"output-description-" + index + "-" + io}>
                <AccordionTrigger className="text-white text-sm inputoutput font-bold tracking-wide">
                    Description
                </AccordionTrigger>
                <AccordionContent>
                    <Textarea
                        value={output?.description ?? ""}
                        onChange={(e) =>
                            setApis(
                                apis.map((apiObj, i) =>
                                    i === index
                                        ? {
                                            ...apiObj,
                                            output: apiObj.output.map((out: any, j: number) =>
                                                j === io ? { ...out, description: e.target.value } : out
                                            ),
                                        }
                                        : apiObj
                                )
                            )
                        }
                    />
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};

export default JsonOutputField;
